import { getName } from '@utils/explorer'
import { Attribute } from './Attributes'
import { Attribute as PrismaAttribute } from '@prisma/client'
import { CompressedNFT } from 'types/solana'

export type AttributeJSON = {
  trait_type: string
  value: string | number
}

type Metadata = {
  attributes: AttributeJSON[]
  description: string
  image: string
  name: string
  sellerFeeBasisPoints: number
  symbol: string
}

type OffChainMetadata = {
  metadata: Metadata
  uri: string
  error: string
}

export type NftResponseData = {
  account: string
  offChainMetadata: OffChainMetadata
}

export interface NftData {
  id?: string
  mintAddress: string
  name: string
  description: string
  number: string
  imageURL: string
  attribute: Attribute
  sellerFeeBasisPoints: number
  symbol: string
  colors?: string[]
  backgroundColor?: string
  listed: boolean
  staked: boolean
}

export type NftDataKeys = keyof NftData

export class Nft {
  static dataFromAttributes(attributes: PrismaAttribute[]) {
    return attributes
      .filter((attribute) => !attribute.name.includes('NONE'))
      .map((attribute, index: number) => ({
        backgroundColor: convertBackgroundToColor(
          getName(attribute?.background, 'background'),
        ),
        listed: false,
        unminted: false,
        imageURL: attribute.dataURL,
        mintAddress: '',
        name: attribute.name,
        description: '',
        number: index.toString(),
        attribute: {
          [attribute.traitName]: attribute.name,
        },
        sellerFeeBasisPoints: 0,
        symbol: '',
        staked: false,
      }))
  }

  static fromJson(json: CompressedNFT) {
    try {
      const content = json.content
      const metadata = content.metadata
      const number = metadata.name.match(/\d+/)

      const attributes = convertAttributes(metadata.attributes as any)

      let imageURL = content.files[0]?.uri || ''

      // find a file with -640 in the name
      if (content.files.find((file) => file.uri.includes('-640'))) {
        imageURL = content.files.find((file) => file.uri.includes('-640'))?.uri
      }

      const data: NftData = {
        id: json.id,
        mintAddress: json?.ownership?.owner,
        name: metadata?.name || '',
        description: metadata?.description || '',
        number: number ? number[0] : '0',
        imageURL,
        attribute: attributes || {},
        sellerFeeBasisPoints: json?.royalty.basis_points || 0,
        symbol: metadata?.symbol || '',
        backgroundColor: convertBackgroundToColor(
          (attributes?.background as string) || '',
        ),
        listed: false,
        staked: false,
      }

      return data
    } catch {}
  }
}

export function convertAttributes(jsonAttributes: Record<string, string>[]) {
  if (!jsonAttributes) return

  const formattedJsonAttributes = jsonAttributes.map((attribute) => {
    const { trait_type, value } = attribute
    return {
      trait_type: trait_type,
      value,
    }
  })

  return Array.from(formattedJsonAttributes).reduce(
    (acc: Record<string, string | number | boolean>, { trait_type, value }) => {
      acc[trait_type.toLocaleLowerCase()] = value
      return acc
    },
    {} as Attribute,
  )
}

export function revertAttributes(attributes: Attribute) {
  return Object.entries(attributes).map(([key, value]) => ({
    trait_type: key,
    value,
  }))
}

const uncapitalizeEachWordAndReplaceSpaceWithDash = (str: string) =>
  str
    .split(' ')
    .map((word) => word.charAt(0).toLowerCase() + word.slice(1))
    .join('-')

export function convertBackgroundToColor(background: string) {
  try {
    return backgroundTraitToColor[
      uncapitalizeEachWordAndReplaceSpaceWithDash(background)
    ]
  } catch (error) {
    return undefined
  }
}

// map background trait to rgb color
const backgroundTraitToColor: Record<string, string> = {
  apricot: '#FBC6A4',
  beige: '#F5F0BB',
  cream: '#F0EADA',
  'cyan-blue': '#A8D1D1',
  'dark-gray': '#212121',
  grape: '#986D8E',
  lavender: '#CAB8FF',
  'leaf-green': '#BCE29E',
  'lemon-yellow': '#FFF89A',
  'light-goldenrod': '#F1F0E1',
  'light-gray': '#EEEBE6',
  'light-green': '#E7FBBE',
  'light-orange': '#F2D388',
  'light-purple': '#F0D9FF',
  'light-tan': '#F3EAD6',
  lilac: '#DEBACE',
  lime: '#9DFF9F',
  'olive-green': '#ADC2A9',
  'pale-brown': '#EFE0CE',
  'pale-green': '#DCE4D7',
  peach: '#F3E0D5',
  pink: '#F1B1E9',
  rose: '#F38BA0',
  salmon: '#FD8A8A',
  'sea-blue': '#80D8FF',
  'sky-blue': '#B9F3FC',
  'steel-blue': '#93BFCF',
  tangerine: '#EEC373',
  'tea-green': '#D3E4CD',
  'teal-blue': '#68A7AD',
  watermelon: '#D57E7E',
  whitesmoke: '#F2F0EA',
  jellyfish: '#181A32',
  lava: '#212121',
  'starry-sky': '#37009C',
  'apricot-waves': '#F8A075',
  'cyan-blue-waves': '#5D9B9B',
  'sea-blue-waves': '#35A8FF',
  'sky-blue-waves': '#6ADBF9',
  'steel-blue-waves': '#467D98',
  'teal-blue-waves': '#235C63',
  sparkles: 'darkgray',
  snowflakes: 'darksalmon',
  leaves: 'darkseagreen',
  'honey-comb': 'gold',
  coins: 'goldenrod',
  paws: 'lavenderblush',
  hearts: 'pink',
  underwater: '#345d97',
  'celestial-blue': '#365fe4',
  'digital-void': '#232721',
  'art-deco-elegance': '#212121',
  eclipse: '#222221',
  'red-good-fortune': '#cf202a',
  'market-charts': '#0b6623',
  'racing-circuit': '#272523',
  'mysterious-night': '#6f4649',
  'pumpkins-red': '#b12004',
  'rain-night': '#33506e',
  'rainy-city': '#1a1b28',
  'clouds-night': '#395978',
  'spider-webs-red': '#ae2103',
  'rain-day': '#ccb5f9',
  'eerie-dawn': '#261634',
  'pumpkins-orange': '#17080b',
  'purple-haze-chair': '#f6b2de',
  'xochimilco-forest': '#1a874e',
  'spaceX-launch': '#478133',
  "jupiter's-orbit": '#44806e',
  'neon-green-portal': '#72fbc7',
  'synth-wave': '#a76deb',
  'ufc-arena': '#251f3d',
  'helius-matrix': '#222328',
  'blazing-sun': '#f17f2a',
  'psychedelic-haze': '#793a80',
  'soccer-field': '#45a446',
  'depin-fireworks': '#031530',
  'leaning-tower-of-pisa': '#7dd130',
  'clouds-sunset': '#fdb353',
  'haunted-harvest': '#220a31',
  'rain-day-window': '#5c2f39',
  'soviet-red': '#261c1a',
  'stage-performance': '#2f56d1',
  '5G-waves': '#aed3f5',
  'dePIN-fireworks': '#031530',
  'enchanted-forest': '#1f8532',
  'rain-night-stars': '#385877',
  'spaceship-interior': '#43586e',
  'nordic-storm': '#508fba',
  'burning-mOBILE': '#ffa151',
  'hNT-galaxy': '#0d7dc9',
  'cyber-pink': '#1e1939',
  'rain-night-window': '#b9bffb',
  'f1-track': '#edeeea',
}
